/**
 * Hook del listado de Flotas con paginación server-side (US-2.3).
 *
 * Espejo de `useCustomers`: filtros + `offset`/`limit` + `meta` del backend, con
 * loading/error/refresh. La red vive en `fleetApi`; aquí solo el estado de la vista.
 */
import { useCallback, useEffect, useState } from 'react';
import type { Fleet, FleetStatus, PageMeta } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { fleetApi, type FleetListParams } from '../api/fleetApi';

/** Límite por página por defecto del Admin Web. */
export const DEFAULT_FLEETS_LIMIT = 20;

/** Filtros del listado (los del contrato, sin paginación). */
export interface FleetFilters {
  q?: string;
  status?: FleetStatus;
}

export interface UseFleetsOptions {
  /** Elementos por página (default `DEFAULT_FLEETS_LIMIT`). */
  limit?: number;
  /** Filtros iniciales. */
  initialFilters?: FleetFilters;
}

export interface UseFleetsResult {
  data: Fleet[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  filters: FleetFilters;
  offset: number;
  limit: number;
  setFilters: (filters: FleetFilters) => void;
  setOffset: (offset: number) => void;
  refresh: () => void;
}

const emptyMeta = (limit: number): PageMeta => ({ offset: 0, limit, total: 0, hasMore: false });

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudieron cargar las flotas.';
}

export function useFleets(options: UseFleetsOptions = {}): UseFleetsResult {
  const limit = options.limit ?? DEFAULT_FLEETS_LIMIT;
  const [filters, setFiltersState] = useState<FleetFilters>(() => options.initialFilters ?? {});
  const [offset, setOffsetState] = useState(0);
  const [data, setData] = useState<Fleet[]>([]);
  const [meta, setMeta] = useState<PageMeta>(() => emptyMeta(limit));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: FleetListParams = { ...filters, offset, limit };
    fleetApi
      .list(params)
      .then((page) => {
        if (cancelled) return;
        setData(page.data);
        setMeta(page.meta);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Descarta respuestas de una página/filtro anterior que lleguen fuera de orden.
    return () => {
      cancelled = true;
    };
  }, [filters, offset, limit, reloadKey]);

  const setFilters = useCallback((next: FleetFilters) => {
    setOffsetState(0);
    setFiltersState(next);
  }, []);

  const setOffset = useCallback((next: number) => {
    setOffsetState(next < 0 ? 0 : next);
  }, []);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { data, meta, loading, error, filters, offset, limit, setFilters, setOffset, refresh };
}
